import type { ReactNode } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { orgKey } from '@/app/queryClient';
import { useOrgId } from '@/app/session';
import { Avatar, Drawer, ErrorState, Icon, LinkButton, LoadingState, StatusDot } from '@/components/ui';
import { humanize } from '@/lib/format';
import './parties.css';
import { ResearchSection } from '@/features/research/ResearchSection';
import { HistoricalCorrespondenceSection } from '@/features/historical-correspondence/HistoricalCorrespondenceSection';
import { HistoricalWorkflowSection } from '@/features/historical-workflow/HistoricalWorkflowSection';
import { HistoricalNotesSection } from '@/features/historical-notes/HistoricalNotesSection';

export type Contact = components['schemas']['Contact'];
export type Company = components['schemas']['Company'];

export function contactQuery(orgId: string, contactId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'contacts', 'detail', contactId),
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/contacts/{contactId}', { params: { path: { orgId, contactId } }, signal })),
  });
}

export function companyQuery(orgId: string, companyId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'companies', 'detail', companyId),
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/companies/{companyId}', { params: { path: { orgId, companyId } }, signal })),
  });
}

export interface PartyDrawerState {
  contact?: string;
  company?: string;
}

const companyName = (c: Company) => c.display_name ?? c.trading_name ?? c.legal_name ?? 'Unknown company';

/**
 * Contact or company opened over the current page (`?contact=` / `?company=`).
 * Moving between people and companies swaps the drawer content in place.
 */
export function PartyDrawers({ search, onChange }: { search: PartyDrawerState; onChange: (next: PartyDrawerState) => void }) {
  if (!search.contact && !search.company) return null;
  return (
    <Drawer open onClose={() => onChange({})} title={<span className="visually-hidden">{search.contact ? 'Person' : 'Company'}</span>}>
      <div className="sheet party-sheet">
        <PartyView party={search} onOpen={onChange} />
      </div>
    </Drawer>
  );
}

/**
 * A person or a company: avatar header, then how to reach them and where they appear. Inside the
 * site panel (`sitesInPlace`) a site opens in that panel over the same page; elsewhere on the board.
 */
export function PartyView({ party, onOpen, sitesInPlace = false }: { party: PartyDrawerState; onOpen: (next: PartyDrawerState) => void; sitesInPlace?: boolean }) {
  if (party.contact) return <ContactView key={party.contact} contactId={party.contact} sitesInPlace={sitesInPlace} onOpenCompany={(id) => onOpen({ company: id })} />;
  if (party.company) return <CompanyView key={party.company} companyId={party.company} sitesInPlace={sitesInPlace} onOpenContact={(id) => onOpen({ contact: id })} />;
  return null;
}

function PartyHero({ avatar, name, sub }: { avatar: ReactNode; name: string; sub?: ReactNode }) {
  return (
    <header className="party-hero">
      {avatar}
      <div className="party-hero-text">
        <h3 className="party-name display">{name}</h3>
        {sub ? <p className="party-sub">{sub}</p> : null}
      </div>
    </header>
  );
}

function PartyBlock({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="party-block">
      <header className="party-block-header">
        <h4>{title}</h4>
        {action}
      </header>
      {children}
    </section>
  );
}

function SiteRow({ id, name, role, inPlace }: { id: string; name: string; role: string; inPlace: boolean }) {
  const content = (
    <>
      <span className="party-row-icon" aria-hidden>
        <Icon name="pin" size={14} />
      </span>
      <span className="party-row-name name truncate">{name}</span>
      <span className="party-row-sub">{humanize(role)}</span>
    </>
  );
  return (
    <li>
      {inPlace ? (
        <Link to="." search={(s) => ({ ...s, site: id, contact: undefined, company: undefined })} resetScroll={false} className="party-row">
          {content}
        </Link>
      ) : (
        <Link to="/app/board" search={{ site: id }} className="party-row">
          {content}
        </Link>
      )}
    </li>
  );
}

function ContactView({ contactId, sitesInPlace, onOpenCompany }: { contactId: string; sitesInPlace: boolean; onOpenCompany: (id: string) => void }) {
  const orgId = useOrgId();
  const q = useQuery(contactQuery(orgId, contactId));
  if (q.isPending) return <LoadingState label="Loading person" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const c = q.data;
  const first = c.affiliations[0];
  return (
    <div className="party">
      <PartyHero
        avatar={<Avatar name={c.display_name} company={first?.company_name} size={56} />}
        name={c.display_name}
        sub={
          c.job_title || first ? (
            <>
              {c.job_title ? <span>{c.job_title}</span> : null}
              {first ? (
                <button type="button" className="party-inline-link" onClick={() => onOpenCompany(first.company_id)}>
                  {first.company_name}
                </button>
              ) : null}
            </>
          ) : undefined
        }
      />
      {c.contact_status === 'do_not_contact' || c.contact_status === 'archived' ? <StatusDot domain="contactStatus" value={c.contact_status} /> : null}
      <PartyBlock title="Email and phone">
        {c.channels.length === 0 ? (
          <p className="faint">No email or phone recorded.</p>
        ) : (
          <ul className="party-list">
            {c.channels.map((ch) => (
              <li key={ch.id} className="party-channel-row">
                <span className="party-channel-kind">{ch.kind === 'email' ? 'Email' : ch.kind === 'url' ? 'Web' : humanize(ch.kind)}</span>
                {ch.kind === 'email' ? (
                  <a className="party-channel" href={`mailto:${ch.value}`}>
                    {ch.value}
                  </a>
                ) : ch.kind === 'url' ? (
                  <a className="party-channel" href={ch.value} target="_blank" rel="noreferrer">
                    {ch.value}
                  </a>
                ) : (
                  <span className="party-channel num">{ch.value}</span>
                )}
                {ch.do_not_contact ? <StatusDot tone="accent">Do not contact</StatusDot> : null}
              </li>
            ))}
          </ul>
        )}
      </PartyBlock>
      {c.affiliations.length ? (
        <PartyBlock title="Companies">
          <ul className="party-list">
            {c.affiliations.map((a) => (
              <li key={`${a.company_id}-${a.role}`}>
                <button type="button" className="party-row" onClick={() => onOpenCompany(a.company_id)}>
                  <Avatar name={a.company_name} company={a.company_name} size={28} className="avatar-company" />
                  <span className="party-row-name name truncate">{a.company_name}</span>
                  <span className="party-row-sub">{humanize(a.role)}</span>
                </button>
              </li>
            ))}
          </ul>
        </PartyBlock>
      ) : null}
      <PartyBlock title="Sites">
        {c.location_links.length === 0 ? (
          <p className="faint">Not linked to a site.</p>
        ) : (
          <ul className="party-list">
            {c.location_links.map((l) => (
              <SiteRow key={`${l.location_id}-${l.role}`} id={l.location_id} name={l.location_name} role={l.role} inPlace={sitesInPlace} />
            ))}
          </ul>
        )}
      </PartyBlock>
      {c.notes ? (
        <PartyBlock title="Notes">
          <p className="party-notes">{c.notes}</p>
        </PartyBlock>
      ) : null}
    </div>
  );
}

function CompanyView({ companyId, sitesInPlace, onOpenContact }: { companyId: string; sitesInPlace: boolean; onOpenContact: (id: string) => void }) {
  const orgId = useOrgId();
  const q = useQuery(companyQuery(orgId, companyId));
  if (q.isPending) return <LoadingState label="Loading company" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const c = q.data;
  const name = companyName(c);
  return (
    <div className="party">
      <PartyHero
        avatar={<Avatar name={name} company={name} size={56} className="avatar-company" />}
        name={name}
        sub={
          <>
            {c.trading_name && c.legal_name !== c.trading_name ? <span>{c.legal_name}</span> : null}
            {c.registration_country ? <span>{c.registration_country}</span> : null}
            {c.website_url ? (
              <a className="party-inline-link" href={c.website_url} target="_blank" rel="noreferrer">
                {c.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            ) : null}
          </>
        }
      />
      <PartyBlock title="Legacy company workflow">
        <p style={{ whiteSpace: 'pre-wrap' }}>{c.legacy_workflow_status ?? 'Not recorded'}</p>
        <p>Historical—not native location stage.</p>
        <Link to="/app/companies">Legacy company workflow</Link>
      </PartyBlock>
      <ResearchSection kind="company" targetId={companyId} />
      <HistoricalNotesSection companyId={companyId} />
      <HistoricalCorrespondenceSection companyId={companyId} /><HistoricalWorkflowSection companyId={companyId} />
      {c.description ? <p className="party-notes">{c.description}</p> : null}
      <PartyBlock title="People">
        {c.contacts.length === 0 ? (
          <p className="faint">No people recorded.</p>
        ) : (
          <ul className="party-list">
            {c.contacts.map((p) => (
              <li key={`${p.contact_id}-${p.role}`}>
                <button type="button" className="party-row" onClick={() => onOpenContact(p.contact_id)}>
                  <Avatar name={p.contact_display_name} company={name} size={28} />
                  <span className="party-row-name name truncate">{p.contact_display_name}</span>
                  <span className="party-row-sub">{humanize(p.role)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </PartyBlock>
      <PartyBlock
        title="Sites"
        action={
          c.location_link_count > 0 ? (
            <LinkButton size="sm" variant="quiet" to="/app/board" search={{ owner: c.id }}>
              Show on board
            </LinkButton>
          ) : null
        }
      >
        {c.locations.length === 0 ? (
          <p className="faint">Not linked to a site.</p>
        ) : (
          <ul className="party-list">
            {c.locations.map((l) => (
              <SiteRow key={`${l.location_id}-${l.role}`} id={l.location_id} name={l.location_name} role={l.role} inPlace={sitesInPlace} />
            ))}
            {c.location_link_count > c.locations.length ? <li className="faint party-more">{c.location_link_count - c.locations.length} more</li> : null}
          </ul>
        )}
      </PartyBlock>
    </div>
  );
}
